// Pen lab deck engine, integrated 2026-10-25. Original local engine.js retained in the lab. MIT.
/* deck engine: a terse JSON slide spec -> eased SVG "magic move" between slides.
   Plain JS, no dependencies, works from file://. MIT.
   Pipeline: spec -> states (objects accumulated slide by slide) -> primitives (line, poly,
   circle, rect, text, each with a stable key "objId/part") -> per-frame interpolation of
   primitives with the same key; new keys enter (draw, grow, fade-rise), lost keys fade. */
const DeckEngine = (function () {
'use strict';
const W = 160, H = 90;
const COL = { paper: '#f2ead8', card: '#e8dcc0', weathered: '#e8dcc0', navy: '#0b2a5b', ink: '#0b2a5b',
  cobalt: '#1a3fa0', ultra: '#2b59d9', ultramarine: '#2b59d9', cyan: '#9ec9e2', sage: '#8fa38a',
  charcoal: '#2a2a28', slate: '#526270', haze: '#687c92', rule: '#b9b49f', persimmon: '#e8663d', warm: '#e8663d' };
const SZ = { xs: 1.8, s: 2.2, m: 2.9, l: 4.2, xl: 6.4, xxl: 11 };
const TYPES = ['lane', 'dot', 'vec', 'arrow', 'text', 'label', 'callout', 'box', 'grid', 'bars', 'brace', 'line'];
const COMMON = ['at', 'side', 'gap', 'dx', 'dy', 'c', 'hi', 'from', 'size', 'o', 'pin'];
const KEYS = {
  lane: ['y', 'x0', 'x1'], dot: ['x', 'y', 'r', 'hollow', 'name', 'ns'], vec: ['keys', 'name', 'fmt', 'bare'],
  arrow: ['text', 'dash', 'end', 'sw', 'pos'], text: ['align', 'italic', 'bold', 'mono'], label: ['align'],
  callout: ['to'], box: ['w', 'h', 'sub', 'mono', 'fill'], grid: ['cell', 'rows', 'cols', 'heat', 'max', 'fmt'],
  bars: ['w', 'h', 'max', 'labels', 'fmt'], brace: ['x', 'text'], line: ['text', 'dash', 'sw'] };
const SLIDE_KEYS = ['put', 'del', 'clear', 'focus', 'cap', 'title', 'kicker', 'fig', 'dur', 'hold', 'stagger', 'note'];
const DIM = 0.22, MONO = 0.6, SERIF = 0.47, VADV = 0.53; // VADV: tighter cells for vector glyphs
const TOP = 29, BOTTOM = 82; // content zone (header above, caption below)

const col = (c, d) => c == null ? d : (COL[c] || c);
const sz = (s, d) => s == null ? d : typeof s === 'number' ? s : (SZ[s] || d);
// "text" is also a property of arrow, line and brace, so it is tried last
const DETECT = TYPES.filter(t => t !== 'text').concat('text');
const typeOf = o => { if (!o || typeof o !== 'object') return null; for (const t of DETECT) if (o[t] !== undefined) return t; return null; };
// a re-put of an existing id: replace if it names a type, unless every key is a property of the old type
function putType(o, prev) {
  const t = typeOf(o);
  if (!t || !prev) return t;
  const pt = typeOf(prev);
  if (t !== pt && Object.keys(o).every(k => COMMON.includes(k) || (KEYS[pt] || []).includes(k))) return null;
  return t;
}
const clamp = (v, a = 0, b = 1) => v < a ? a : v > b ? b : v;
const isNum = v => typeof v === 'number' && isFinite(v);
function glob(p, id) {
  if (p === id) return true;
  if (!String(p).includes('*')) return false;
  return new RegExp('^' + String(p).split('*').map(s => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$').test(id);
}
function fmtNum(v, d) { return typeof v === 'number' ? (d == null ? String(+v.toFixed(4)) : v.toFixed(d)) : String(v); }
function decOf(s) { const m = String(s).match(/\.(\d+)$/); return m ? m[1].length : 0; }

/* ---------------------------------------------------------------- states */
function buildStates(deck) {
  const errs = [], warns = [], states = [];
  const ever = new Set();
  let objs = new Map();
  let head = { kicker: deck.kicker || '', title: deck.title || '', fig: deck.fig || '', cap: deck.cap || '' };
  (deck.slides || []).forEach((s, n) => {
    const where = `slide ${n + 1}`;
    for (const k of Object.keys(s)) if (!SLIDE_KEYS.includes(k) && !['objs', 'add', 'set'].includes(k)) warns.push(`${where}: unknown slide key "${k}"`);
    objs = s.clear ? new Map() : new Map(objs);
    head = Object.assign({}, head);
    for (const k of ['kicker', 'title', 'fig', 'cap']) if (s[k] !== undefined) head[k] = s[k];
    for (const pat of [].concat(s.del || [])) {
      let hit = false;
      for (const id of [...objs.keys()]) if (glob(pat, id)) { objs.delete(id); hit = true; }
      if (!hit) warns.push(`${where}: del "${pat}" matched nothing`);
    }
    const put = s.put || s.objs || s.add || s.set || {};
    for (const [id, o] of Object.entries(put)) {
      if (o === null) { objs.delete(id); continue; }
      if (/[.@\s]/.test(id)) errs.push(`${where}: id "${id}" must not contain ".", "@" or spaces`);
      const prev = objs.get(id), t = putType(o, prev);
      // same id: same type (or no type key) merges, a different type replaces; null drops a property
      const next = prev && (!t || t === typeOf(prev)) ? Object.assign({}, prev, o) : Object.assign({}, o);
      for (const k of Object.keys(next)) if (next[k] === null) delete next[k];
      if (!t && !prev) { errs.push(`${where}: "${id}" has no type key (one of ${TYPES.join(', ')})`); continue; }
      const tt = typeOf(next);
      for (const k of Object.keys(o)) if (k !== tt && !COMMON.includes(k) && !(KEYS[tt] || []).includes(k)) warns.push(`${where}: "${id}" (${tt}) ignores key "${k}"`);
      if ('hi' in o) next._hi = n;
      if ('from' in o) next._from = n;
      objs.set(id, next);
      ever.add(id);
    }
    states.push({ n, head: Object.assign({}, head), objs, focus: s.focus, dur: s.dur, hold: s.hold, stagger: s.stagger });
  });
  return { states, errs, warns, ever };
}

/* ---------------------------------------------------------------- text helpers */
function textW(str, size, mono, ls, italic) {
  const n = String(str).length;
  return mono ? n * (MONO + (ls || 0)) * size : n * (italic ? 0.44 : SERIF) * size;
}
// text primitive whose visual middle sits at cy (or whose baseline is opt.base)
function textP(str, x, cy, o) {
  const size = o.size;
  const p = { type: 'text', x, y: o.base != null ? o.base : cy + 0.34 * size, str: String(str), size,
    mono: !!o.mono, fill: o.fill, anchor: o.anchor || 'middle', o: 1 };
  if (o.italic) p.italic = 1;
  if (o.weight) p.weight = o.weight;
  if (o.ls) p.ls = o.ls;
  if (o.roll) p.roll = 1;
  if (o.tween) p.tween = 1;
  if (o.runs) p.runs = o.runs;
  if (o.halo) p.halo = 1;
  return p;
}
function textBox(p) {
  const w = p.runs ? p.runs.reduce((s, r) => s + textW(r.t, p.size, p.mono, p.ls, r.italic), 0) : textW(p.str, p.size, p.mono, p.ls, p.italic);
  const x0 = p.anchor === 'start' ? p.x : p.anchor === 'end' ? p.x - w : p.x - w / 2;
  const cy = p.y - 0.34 * p.size;
  return { x0, x1: x0 + w, y0: cy - 0.45 * p.size, y1: cy + 0.45 * p.size, cx: x0 + w / 2, cy };
}
/* ---- geometry: what a primitive occupies (used by auto-layout and by check.js) */
function shapesOf(p) {
  if (p.type === 'text') { const b = textBox(p); return [{ k: 'b', x0: b.x0 + 0.15, y0: b.y0 + 0.12, x1: b.x1 - 0.15, y1: b.y1 - 0.12, text: 1 }]; }
  if (p.type === 'rect') return p.w > 0 && p.h > 0 ? [{ k: 'b', x0: p.x, y0: p.y, x1: p.x + p.w, y1: p.y + p.h }] : [];
  if (p.type === 'circle') return [{ k: 'b', x0: p.cx - p.r, y0: p.cy - p.r, x1: p.cx + p.r, y1: p.cy + p.r }];
  if (p.type === 'line') return [{ k: 's', x1: p.x1, y1: p.y1, x2: p.x2, y2: p.y2 }];
  if (p.type === 'poly') {
    const P = p.pts, out = [];
    if (p.closed) { let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity; for (let i = 0; i < P.length; i += 2) { x0 = Math.min(x0, P[i]); x1 = Math.max(x1, P[i]); y0 = Math.min(y0, P[i + 1]); y1 = Math.max(y1, P[i + 1]); } return [{ k: 'b', x0, y0, x1, y1 }]; }
    for (let i = 0; i + 3 < P.length; i += 2) out.push({ k: 's', x1: P[i], y1: P[i + 1], x2: P[i + 2], y2: P[i + 3] });
    return out;
  }
  return [];
}
function boxOverlap(a, b) {
  const ox = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0), oy = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
  return ox > 0.2 && oy > 0.2 ? ox * oy : 0;
}
function segHitsBox(L, b) { // Liang-Barsky clip of segment L against box b
  let t0 = 0, t1 = 1;
  const dx = L.x2 - L.x1, dy = L.y2 - L.y1;
  const tests = [[-dx, L.x1 - b.x0], [dx, b.x1 - L.x1], [-dy, L.y1 - b.y0], [dy, b.y1 - L.y1]];
  for (const [pp, q] of tests) {
    if (pp === 0) { if (q < 0) return false; continue; }
    const r = q / pp;
    if (pp < 0) { if (r > t1) return false; if (r > t0) t0 = r; } else { if (r < t0) return false; if (r < t1) t1 = r; }
  }
  return t1 - t0 > 1e-6;
}
// how badly box b collides with a list of shapes (0 = free)
function collide(b, shapes) {
  let c = 0;
  for (const sh of shapes) {
    if (sh.k === 'b') { const a = boxOverlap(b, sh); if (a) c += 3 + a * (sh.text ? 2 : 0.5); }
    else if (segHitsBox(sh, b)) c += 2;
  }
  return c;
}
function splitRuns(str, fill, italicFill, weight) {
  const s = String(str), i = s.indexOf(' | ');
  if (i < 0) return null;
  return [{ t: s.slice(0, i), fill, weight }, { t: ' ' + s.slice(i + 3), fill: italicFill, italic: 1, weight: 400 }];
}

/* ---------------------------------------------------------------- layout */
function layoutState(st, ctx) {
  const A = Object.create(null), out = Object.create(null);
  const errs = [], warns = [];
  const busy = new Set();
  let cur = '';
  // occupancy for auto-layout: shapes of objects already laid out (+ last pass's shapes of objects not laid out yet)
  const occ = [];
  const extra = ctx.extra || [];
  const free = (b, ignore) => {
    const sh = [];
    for (const x of occ) if (x.obj !== cur && !(ignore && ignore.includes(x.obj))) sh.push(x);
    for (const x of extra) if (x.obj !== cur && !(x.obj in A) && !busy.has(x.obj) && !(ignore && ignore.includes(x.obj))) sh.push(x);
    let c = collide(b, sh);
    if (b.x0 < 6 || b.x1 > 154 || b.y0 < TOP || b.y1 > BOTTOM) c += 50;
    return c;
  };
  const E = m => errs.push(`slide ${st.n + 1}: ${cur ? '"' + cur + '" ' : ''}${m}`);
  const objs = st.objs;

  function need(id) {
    if (id in A) return A[id];
    if (!objs.has(id)) {
      const m = `refers to missing "${id}"`;
      if (ctx.ever.has(id)) warns.push(`slide ${st.n + 1}: "${cur}" dropped, ${m} (deleted earlier)`); else E(m);
      return null;
    }
    if (busy.has(id)) { E(`circular reference via "${id}"`); return null; }
    const save = cur; busy.add(id); cur = id;
    try { lay(id, objs.get(id)); } catch (e) { E('failed: ' + e.message); A[id] = null; }
    for (const p of out[id] || []) for (const sh of shapesOf(p)) { sh.obj = id; occ.push(sh); }
    busy.delete(id); cur = save;
    if (!(id in A)) A[id] = null;
    return A[id];
  }
  const pt = (x, y) => ({ kind: 'pt', x0: x, x1: x, y0: y, y1: y, cx: x, cy: y });
  function get(ref) {
    if (Array.isArray(ref)) { if (ref.length >= 2 && isNum(ref[0]) && isNum(ref[1])) return pt(ref[0], ref[1]); E(`bad point ${JSON.stringify(ref)}`); return null; }
    if (typeof ref !== 'string') { E(`bad reference ${JSON.stringify(ref)}`); return null; }
    const m = ref.match(/^([^.@]+)@(-?[\d.]+)$/);
    if (m) { const L = need(m[1]); if (!L) return null; return pt(+m[2], L.cy); }
    const [id, ...rest] = ref.split('.');
    const T = need(id);
    if (!T || !rest.length) return T;
    const part = rest.join('.');
    if (!T.parts || !T.parts[part]) { E(`"${ref}": ${id} has no part "${part}"`); return null; }
    return T.parts[part];
  }
  // centre of a w x h object placed by o.at / o.side / o.gap / o.dx / o.dy.
  // opt.auto: if that spot collides, try other sides / gaps (relative) or small nudges (absolute) and take the freest.
  function place(o, w, h, defSide, opt) {
    const first = place1(o, w, h, defSide);
    if (!first || !opt || !opt.auto || o.pin) return first;
    const padL = opt.padL || 0;
    const cost = q => free({ x0: q.cx - w / 2 - padL, y0: q.cy - h / 2, x1: q.cx + w / 2, y1: q.cy + h / 2 }, opt.ignore) + (opt.extraCost ? opt.extraCost(q) : 0);
    let best = first, bc = cost(first);
    if (bc === 0) return first;
    const cands = [];
    if (first.rel) {
      const T = get(o.at), arrow = T && T.kind === 'arrow';
      const s0 = o.side || (arrow ? 'label' : defSide), g = o.gap != null ? o.gap : 1.2;
      const opp = { n: 's', s: 'n', e: 'w', w: 'e', ne: 'sw', sw: 'ne', nw: 'se', se: 'nw', label: 'label' };
      const sides = [s0, opp[s0] || 's', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
      for (const gg of [g, g + 1.4, g + 3]) for (const sd of sides) {
        if (arrow && sd === 'label') { cands.push(Object.assign({}, o, { gap: gg, side: 'label' })); cands.push(Object.assign({}, o, { gap: gg, side: 'label', _flip: 1 })); }
        else if (sd !== 'label') cands.push(Object.assign({}, o, { gap: gg, side: sd }));
      }
    } else {
      for (const [ddx, ddy] of [[0, -1.6], [0, 1.6], [0, -3.2], [0, 3.2], [-3, 0], [3, 0], [0, -4.8], [0, 4.8], [-6, 0], [6, 0], [-4, -3], [4, -3], [-4, 3], [4, 3]])
        cands.push(Object.assign({}, o, { dx: (o.dx || 0) + ddx, dy: (o.dy || 0) + ddy }));
    }
    const nW = warns.length;
    cands.forEach((c, i) => {
      const q = place1(c, w, h, defSide); if (!q) return;
      const k = cost(q) + (i + 1) * 0.02;
      if (k < bc) { bc = k; best = q; }
    });
    warns.length = nW;
    return best;
  }
  function place1(o, w, h, defSide) {
    let cx, cy, side = null;
    if (o.at == null) { E('needs "at"'); return null; }
    if (Array.isArray(o.at)) { const T = get(o.at); if (!T) return null; cx = T.cx; cy = T.cy; }
    else {
      const T = get(o.at); if (!T) return null;
      const g = o.gap != null ? o.gap : 1.2;
      side = o.side || (T.kind === 'arrow' ? 'label' : defSide);
      if (side === 'label' && T.kind === 'arrow') {
        const f = o._flip ? -1 : 1, nx = T.nx * f, ny = T.ny * f;
        cx = T.cx + nx * (g + 0.5 * (Math.abs(nx) * w + Math.abs(ny) * h));
        cy = T.cy + ny * (g + 0.5 * (Math.abs(nx) * w + Math.abs(ny) * h));
        side = nx > 0.3 ? 'e' : nx < -0.3 ? 'w' : 'n';
        return finish(cx, cy, side, o, w, h);
      }
      if (side === 'label') side = defSide;
      {
        cx = T.cx; cy = T.cy;
        if (side.includes('n')) cy = T.y0 - g - h / 2;
        if (side.includes('s')) cy = T.y1 + g + h / 2;
        if (side.includes('e')) cx = T.x1 + g + w / 2;
        if (side.includes('w')) cx = T.x0 - g - w / 2;
        if (!/^(n|s|e|w|ne|nw|se|sw|c)$/.test(side)) { E(`bad side "${side}"`); }
      }
    }
    return finish(cx, cy, side, o, w, h);
  }
  function finish(cx, cy, side, o, w, h) {
    cx += o.dx || 0; cy += o.dy || 0;
    // forgive: keep the object inside the content zone (never over the header or the caption)
    const ccx = clamp(cx, 6 + w / 2, 154 - w / 2), ccy = clamp(cy, TOP + 0.5 + h / 2, BOTTOM - 0.5 - h / 2);
    if (Math.abs(ccx - cx) > 0.05 || Math.abs(ccy - cy) > 0.05) warns.push(`slide ${st.n + 1}: "${cur}" nudged inside the plate`);
    return { cx: ccx, cy: ccy, side, rel: !Array.isArray(o.at) };
  }
  // where to put a text's anchor x, given its placement
  function anchorX(pos, w, align) {
    if (pos.rel) {
      if (pos.side && pos.side.includes('e')) return [pos.cx - w / 2, 'start'];
      if (pos.side && pos.side.includes('w')) return [pos.cx + w / 2, 'end'];
      return [pos.cx, 'middle'];
    }
    if (align === 'l') return [pos.cx, 'start'];
    if (align === 'r') return [pos.cx, 'end'];
    return [pos.cx, 'middle'];
  }
  // a point on T's outline in the direction of (tx, ty)
  function edge(T, tx, ty, pad) {
    const dx = tx - T.cx, dy = ty - T.cy, L = Math.hypot(dx, dy) || 1;
    if (T.kind === 'pt') return { x: T.cx, y: T.cy };
    if (T.r != null) return { x: T.cx + dx / L * (T.r + pad), y: T.cy + dy / L * (T.r + pad) };
    const hw = (T.x1 - T.x0) / 2 + pad, hh = (T.y1 - T.y0) / 2 + pad;
    const s = Math.min(dx ? hw / Math.abs(dx) : Infinity, dy ? hh / Math.abs(dy) : Infinity);
    return { x: T.cx + dx * s, y: T.cy + dy * s };
  }

  function lay(id, o) {
    const t = typeOf(o);
    const P = out[id] = [];
    const hiOn = o._hi === st.n;
    const hiAll = hiOn && o.hi === true;
    const hiSet = new Set(hiOn && Array.isArray(o.hi) ? o.hi.map(v => Array.isArray(v) ? v.join('.') : String(v)) : []);
    const HOT = COL.persimmon;
    const add = (part, p, extra) => { p.k = id + '/' + part; p.obj = id; if (p.o == null) p.o = 1; if (o.o != null) p.o *= o.o; if (extra) Object.assign(p, extra); P.push(p); return p; };
    const box = (x0, y0, x1, y1, extra) => Object.assign({ x0, y0, x1, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 }, extra || {});

    if (t === 'lane') {
      if (!isNum(o.y)) { E('lane needs a numeric "y"'); return; }
      const x0 = isNum(o.x0) ? o.x0 : 28, x1 = isNum(o.x1) ? o.x1 : 150, y = o.y;
      const c = hiAll ? HOT : col(o.c, COL.haze);
      add('line', { type: 'line', x1: x0, y1: y, x2: x1, y2: y, stroke: c, sw: 0.18 }, { w: [0, 0.9] });
      add('name', textP(String(o.lane).toUpperCase(), x0 - 2.4, y, { mono: 1, size: sz(o.size, 2.1), fill: hiAll ? HOT : COL.slate, anchor: 'end', ls: 0.06 }));
      A[id] = box(x0, y - 0.6, x1, y + 0.6, { kind: 'lane' });
      return;
    }
    if (t === 'dot') {
      let cx, cy;
      if (typeof o.dot === 'string') {
        const L = get(o.dot); if (!L) return;
        if (!isNum(o.x)) { E('dot on a lane needs a numeric "x"'); return; }
        cx = o.x; cy = L.cy;
      } else if (Array.isArray(o.dot)) { [cx, cy] = o.dot; }
      else { E('dot must be a lane id or [x, y]'); return; }
      const r = isNum(o.r) ? o.r : 1.0, c = hiAll ? HOT : col(o.c, COL.navy);
      add('dot', { type: 'circle', cx, cy, r, fill: o.hollow ? COL.paper : c, stroke: c, sw: o.hollow ? 0.3 : 0 });
      A[id] = box(cx - r, cy - r, cx + r, cy + r, { kind: 'dot', r, cx, cy });
      if (o.name != null) {
        const s = 2.0, w = textW(o.name, s, 1);
        const pos = place({ at: id, side: o.ns || 's', gap: 0.9 }, w, s * 0.9, 's', { auto: 1 });
        const [x, anchor] = anchorX(pos, w);
        add('name', textP(o.name, x, pos.cy, { mono: 1, size: s, fill: hiAll ? HOT : COL.haze, anchor, halo: 1 }));
      }
      return;
    }
    if (t === 'vec') {
      if (!Array.isArray(o.vec)) { E('vec must be an array'); return; }
      const s = sz(o.size, 2.9), adv = s * VADV;
      const vals = o.vec.map(v => fmtNum(v, o.fmt));
      // one-char glyphs sit in tight cells (VADV); longer entries (0.53, 12) need full mono width
      const gw = str => str.length === 1 ? adv : str.length * s * MONO;
      const bare = !!o.bare;
      const w = (bare ? 0 : 2 * adv) + vals.reduce((a, v) => a + gw(v), 0) + Math.max(0, vals.length - 1) * (bare ? 0.6 * adv : adv), h = s * 0.95;
      const nameW = o.name != null ? adv * 0.5 + textW(o.name, Math.max(1.8, s * 0.62), 1) : 0;
      const pos = place(o, w, h, 'n', { auto: 1, padL: nameW }); if (!pos) return;
      const c = hiAll ? HOT : col(o.c, COL.navy);
      let x = pos.cx - w / 2;
      const x0 = x, cy = pos.cy, parts = {};
      const glyph = (part, str, fill, roll) => {
        const g = gw(str);
        const p = add(part, textP(str, x + g / 2, cy, { mono: 1, size: s, fill, roll, halo: 1 }));
        const bx = box(x, cy - h / 2, x + g, cy + h / 2, { kind: 'part' });
        x += g;
        return [p, bx];
      };
      if (!bare) glyph('[', '[', c);
      vals.forEach((v, i) => {
        if (i) { if (bare) x += 0.6 * adv; else glyph(',' + i, ',', c); }
        const [, bx] = glyph('e' + i, v, hiSet.has(String(i)) ? HOT : c, true);
        parts[i] = bx;
        if (o.keys && o.keys[i] != null) add('k' + i, textP(o.keys[i], bx.cx, pos.side && pos.side.includes('n') ? cy - h / 2 - s * 0.4 : cy + h / 2 + s * 0.45, { mono: 1, size: Math.max(1.5, s * 0.5), fill: COL.haze }));
      });
      if (!bare) glyph(']', ']', c);
      if (o.name != null) add('name', textP(o.name, x0 - adv * 0.5, cy, { mono: 1, size: Math.max(1.8, s * 0.62), fill: COL.slate, anchor: 'end' }));
      const nx0 = o.name != null ? x0 - adv * 0.5 - textW(o.name, Math.max(1.8, s * 0.62), 1) : x0; // the name counts for placing others
      A[id] = box(nx0, cy - h / 2, x0 + w, cy + h / 2, { kind: 'vec', parts });
      return;
    }
    if (t === 'arrow') {
      if (!Array.isArray(o.arrow) || o.arrow.length !== 2) { E('arrow must be [from, to]'); return; }
      const F = get(o.arrow[0]), T = get(o.arrow[1]); if (!F || !T) return;
      const S = edge(F, T.cx, T.cy, 0.5), Q = edge(T, F.cx, F.cy, 0.5);
      const dx = Q.x - S.x, dy = Q.y - S.y, L = Math.hypot(dx, dy) || 1, ux = dx / L, uy = dy / L;
      const nx = uy, ny = -ux; // normal: right of a downward arrow, above a rightward one
      const c = hiAll ? HOT : col(o.c, COL.cobalt), sw = isNum(o.sw) ? o.sw : 0.22;
      const dash = o.dash ? '1.1 0.8' : null, end = o.end || 'head';
      let lx = Q.x, ly = Q.y;
      if (end === 'head') {
        const hl = 1.9, hw = 0.72, bx = Q.x - ux * hl, by = Q.y - uy * hl;
        lx = bx + ux * 0.2; ly = by + uy * 0.2;
        add('head', { type: 'poly', closed: 1, pts: [Q.x, Q.y, bx + nx * hw, by + ny * hw, bx - nx * hw, by - ny * hw], fill: c, stroke: 'none' }, { w: [0.7, 1] });
      } else if (end === 'ring') {
        lx = Q.x - ux * 1.7; ly = Q.y - uy * 1.7;
        add('ring', { type: 'circle', cx: Q.x - ux * 0.85, cy: Q.y - uy * 0.85, r: 0.8, fill: COL.paper, stroke: c, sw: 0.22 }, { w: [0.7, 1] });
      } else if (end === 'x') {
        const k = 0.75;
        add('x1', { type: 'line', x1: Q.x - k, y1: Q.y - k, x2: Q.x + k, y2: Q.y + k, stroke: c, sw: 0.28 }, { w: [0.7, 1] });
        add('x2', { type: 'line', x1: Q.x - k, y1: Q.y + k, x2: Q.x + k, y2: Q.y - k, stroke: c, sw: 0.28 }, { w: [0.75, 1] });
      }
      add('line', { type: 'line', x1: S.x, y1: S.y, x2: lx, y2: ly, stroke: c, sw, dash }, { w: [0, 0.8] });
      const f = isNum(o.pos) ? clamp(o.pos, 0.05, 0.95) : 0.5;
      const mx = mix(S.x, Q.x, f), my = mix(S.y, Q.y, f);
      if (o.text != null && o.text !== '') {
        const s = sz(o.size, 2.1);
        const mk = (ff, flip, gap = 0) => {
          const ax = mix(S.x, Q.x, ff), ay = mix(S.y, Q.y, ff), qx = nx * flip, qy = ny * flip;
          const off = 1.3 + (o.gap || 0) + gap + 0.5 * Math.abs(qy) * s * 0.9;
          const anchor = qx > 0.3 ? 'start' : qx < -0.3 ? 'end' : 'middle';
          return textP(o.text, ax + qx * off, ay + qy * off, { mono: 1, size: s, fill: c, anchor, halo: 1 });
        };
        let best = mk(f, 1);
        if (!o.pin) {
          const own = P.flatMap(shapesOf); // never sit on our own line
          const cost = p => { const b = textBox(p); const bb = { x0: b.x0 + 0.15, y0: b.y0 + 0.12, x1: b.x1 - 0.15, y1: b.y1 - 0.12 }; return free(bb) + collide(bb, own); };
          let bc = cost(best), i = 0;
          if (bc > 0) for (const gap of [0, 1.5, 3, 4.5]) for (const d of [0, 0.12, -0.12, 0.24, -0.24, 0.36, -0.36]) for (const flip of [1, -1]) {
            i++;
            const ff = clamp(f + d, 0.1, 0.9), p = mk(ff, flip, gap), k = cost(p) + i * 0.0001 + Math.abs(d) * 0.02 + gap * 0.01 + (flip < 0 ? 0.005 : 0);
            if (k < bc) { bc = k; best = p; }
          }
          if (bc > 0) { const p = mk(f, -1), k = cost(p) + 0.05; if (k < bc) best = p; }
        }
        add('label', best, { w: [0.6, 1] });
      }
      A[id] = box(Math.min(S.x, Q.x), Math.min(S.y, Q.y), Math.max(S.x, Q.x), Math.max(S.y, Q.y), { kind: 'arrow', cx: mx, cy: my, nx, ny });
      return;
    }
    if (t === 'text' || t === 'label') {
      const isLabel = t === 'label';
      const mono = isLabel || !!o.mono;
      const s = sz(o.size, isLabel ? 2.1 : 3.2);
      const raw = String(isLabel ? o.label : o.text);
      const str = isLabel ? raw.toUpperCase() : raw;
      const ls = isLabel ? 0.1 : 0;
      const fill = hiAll ? HOT : col(o.c, isLabel ? COL.slate : COL.charcoal);
      const runs = !mono ? splitRuns(str, fill, COL.cobalt, o.bold ? 600 : null) : null;
      const w = textW(str, s, mono, ls, o.italic);
      // auto-place, but measure the box where the text will really sit (align shifts it for absolute positions)
      const shift = !Array.isArray(o.at) ? 0 : (o.align === 'l' ? w / 2 : o.align === 'r' ? -w / 2 : 0);
      const pos = place(shift ? Object.assign({}, o, { dx: (o.dx || 0) + shift }) : o, w, s * 0.9, 'n', { auto: 1 }); if (!pos) return;
      if (shift) pos.cx -= shift;
      const [x, anchor] = anchorX(pos, w, o.align);
      const p = add('t', textP(str, x, pos.cy, { mono, size: s, fill, anchor, ls, italic: o.italic, weight: o.bold ? 600 : null, runs, tween: !mono ? 0 : 1, halo: 1 }));
      const b = textBox(p);
      A[id] = Object.assign(b, { kind: 'text' });
      return;
    }
    if (t === 'callout') {
      const s = sz(o.size, 2.0), str = String(o.callout), tw = textW(str, s, 1, 0.02);
      const bw = tw + 2.4, bh = s + 1.9;
      const T = o.to != null ? get(o.to) : null;
      if (o.to != null && !T) return;
      let pos;
      // the leader should not cut through other text either
      const tgt = o.to != null ? String(o.to).split(/[.@]/)[0] : null;
      const leadCost = q => {
        if (!T) return 0;
        const L = { k: 's', x1: q.cx, y1: q.cy, x2: T.cx, y2: T.cy };
        let c = 0;
        for (const x of occ) if (x.text && x.obj !== tgt && x.obj !== id && segHitsBox(L, x)) c += 1.5;
        return c;
      };
      if (o.at != null) pos = place(o, bw, bh, 'n', { auto: 1, extraCost: leadCost });
      else if (T) pos = place({ at: o.to, side: o.side || 'n', gap: o.gap != null ? o.gap : 3.2, dx: o.dx, dy: o.dy, pin: o.pin }, bw, bh, 'n', { auto: 1, extraCost: leadCost });
      else { E('callout needs "to" or "at"'); return; }
      if (!pos) return;
      const hot = hiAll || o.c === 'persimmon' || o.c === 'warm';
      const c = hiAll ? HOT : col(o.c, COL.navy);
      const x0 = pos.cx - bw / 2, y0 = pos.cy - bh / 2;
      add('box', { type: 'rect', x: x0, y: y0, w: bw, h: bh, rx: 0.3, fill: COL.paper, stroke: hot ? c : COL.rule, sw: 0.16 });
      add('t', textP(str, pos.cx, pos.cy, { mono: 1, size: s, fill: c, ls: 0.02 }), { w: [0.3, 1] });
      const B = box(x0, y0, x0 + bw, y0 + bh, { kind: 'callout' });
      if (T) {
        const a = edge(B, T.cx, T.cy, 0), b = edge(T, B.cx, B.cy, 0.5);
        if (Math.hypot(a.x - b.x, a.y - b.y) > 0.8 && !(T.cx >= B.x0 && T.cx <= B.x1 && T.cy >= B.y0 && T.cy <= B.y1)) {
          add('lead', { type: 'line', x1: a.x, y1: a.y, x2: b.x, y2: b.y, stroke: hot ? c : COL.slate, sw: 0.12 }, { w: [0.4, 1] });
          add('pin', { type: 'circle', cx: b.x, cy: b.y, r: 0.32, fill: hot ? c : COL.slate, stroke: 'none', sw: 0 }, { w: [0.8, 1] });
        }
      }
      A[id] = B;
      return;
    }
    if (t === 'box') {
      const mono = !!o.mono, s = sz(o.size, mono ? 2.0 : 2.6), str = String(o.box);
      const ss = 1.7;
      const w = isNum(o.w) ? o.w : Math.max(textW(str, s, mono), o.sub ? textW(o.sub, ss, 1) : 0) + 4;
      const h = isNum(o.h) ? o.h : (o.sub ? s * 2.0 + ss : s * 2.3);
      const pos = place(o, w, h, 'e'); if (!pos) return;
      const x0 = pos.cx - w / 2, y0 = pos.cy - h / 2;
      const stroke = hiAll ? HOT : col(o.c, COL.rule);
      add('rect', { type: 'rect', x: x0, y: y0, w, h, rx: 0.5, fill: col(o.fill, COL.card), stroke, sw: hiAll ? 0.3 : 0.16 });
      if (str) add('t', textP(str, pos.cx, o.sub ? pos.cy - ss * 0.55 : pos.cy, { mono, size: s, fill: hiAll ? HOT : (o.c && o.c !== 'rule' ? stroke : COL.navy) }), { w: [0.3, 1] });
      if (o.sub) add('sub', textP(o.sub, pos.cx, pos.cy + s * 0.75, { mono: 1, size: ss, fill: COL.slate }), { w: [0.4, 1] });
      A[id] = box(x0, y0, x0 + w, y0 + h, { kind: 'box' });
      return;
    }
    if (t === 'grid') {
      const rows = o.grid;
      if (!Array.isArray(rows) || !rows.every(Array.isArray)) { E('grid must be an array of rows'); return; }
      const nr = rows.length, nc = Math.max(...rows.map(r => r.length)), cell = isNum(o.cell) ? o.cell : 5;
      const w = nc * cell, h = nr * cell;
      const pos = place(o, w, h, 'e'); if (!pos) return;
      const x0 = pos.cx - w / 2, y0 = pos.cy - h / 2;
      const nums = rows.flat().filter(isNum).map(Math.abs);
      const mx = isNum(o.max) ? o.max : (nums.length ? Math.max(...nums) : 1) || 1;
      const s = sz(o.size, Math.min(2.6, cell * 0.42)), parts = {};
      rows.forEach((row, r) => row.forEach((v, c) => {
        const cx = x0 + c * cell, cy = y0 + r * cell, hot = hiAll || hiSet.has(`${r}.${c}`);
        let fill = 'none', fo = 0, tf = col(o.c, COL.navy);
        if (o.heat && isNum(v)) { fill = COL.cobalt; fo = 0.06 + 0.8 * Math.abs(v) / mx; if (fo > 0.5) tf = COL.paper; }
        add(`c${r}_${c}`, { type: 'rect', x: cx, y: cy, w: cell, h: cell, rx: 0, fill, fo, stroke: hot ? HOT : COL.rule, sw: hot ? 0.35 : 0.13 });
        if (v !== null && v !== '') add(`t${r}_${c}`, textP(fmtNum(v, o.fmt), cx + cell / 2, cy + cell / 2, { mono: 1, size: s, fill: hot && !o.heat ? HOT : tf, roll: 1 }), { w: [0.3, 1] });
        parts[`${r}.${c}`] = box(cx, cy, cx + cell, cy + cell, { kind: 'part' });
      }));
      if (o.rows) o.rows.forEach((lb, r) => add('r' + r, textP(lb, x0 - 1.2, y0 + (r + 0.5) * cell, { mono: 1, size: 1.8, fill: COL.slate, anchor: 'end' })));
      if (o.cols) o.cols.forEach((lb, c) => add('h' + c, textP(lb, x0 + (c + 0.5) * cell, y0 - 1.6, { mono: 1, size: 1.8, fill: COL.slate })));
      A[id] = box(x0, y0, x0 + w, y0 + h, { kind: 'grid', parts });
      return;
    }
    if (t === 'bars') {
      const vals = o.bars;
      if (!Array.isArray(vals) || !vals.every(isNum)) { E('bars must be an array of numbers'); return; }
      const n = vals.length, w = isNum(o.w) ? o.w : n * 7, h = isNum(o.h) ? o.h : 20;
      const mx = isNum(o.max) ? o.max : Math.max(...vals.map(Math.abs)) || 1;
      const pos = place(o, w, h, 'e'); if (!pos) return;
      const x0 = pos.cx - w / 2, base = pos.cy + h / 2, slot = w / n, bw = slot * 0.62, parts = {};
      const d = o.fmt != null ? o.fmt : (vals.every(Number.isInteger) ? 0 : 2);
      add('base', { type: 'line', x1: x0 - 1, y1: base, x2: x0 + w + 1, y2: base, stroke: COL.rule, sw: 0.15 });
      vals.forEach((v, i) => {
        const bh = Math.max(0, h * v / mx), bx = x0 + slot * i + (slot - bw) / 2, hot = hiAll || hiSet.has(String(i));
        add('b' + i, { type: 'rect', x: bx, y: base - bh, w: bw, h: bh, rx: 0, fill: hot ? HOT : col(o.c, COL.cobalt), fo: 0.9, stroke: 'none', sw: 0 }, { ent: 'up' });
        add('v' + i, textP(fmtNum(v, d), bx + bw / 2, base - bh - 1.5, { mono: 1, size: sz(o.size, 1.9), fill: hot ? HOT : COL.navy, tween: 1 }), { w: [0.4, 1] });
        if (o.labels && o.labels[i] != null) add('l' + i, textP(o.labels[i], bx + bw / 2, base + 2.1, { mono: 1, size: 1.8, fill: COL.slate }));
        parts[i] = box(bx, base - bh, bx + bw, base, { kind: 'part' });
      });
      A[id] = box(x0, base - h, x0 + w, base, { kind: 'bars', parts });
      return;
    }
    if (t === 'brace') {
      if (!Array.isArray(o.brace) || o.brace.length !== 2) { E('brace must be [a, b] (lane ids or y values)'); return; }
      const ys = [], targets = [];
      for (const r of o.brace) { if (isNum(r)) ys.push(r); else { const T = get(r); if (!T) return; targets.push(T); ys.push(T.y0, T.y1); } }
      // A brace around two cards on the same row spans their x extents.
      // Lanes and numeric y ranges retain the original vertical bracket.
      if (targets.length === 2 && Math.abs(targets[0].cy - targets[1].cy) < 0.5 && Math.abs(targets[0].cx - targets[1].cx) > 1) {
        const x0 = Math.min(...targets.map(t => t.x0)), x1 = Math.max(...targets.map(t => t.x1));
        const above = o.side === 'n', dir = above ? -1 : 1;
        const y = (above ? Math.min(...ys) : Math.max(...ys)) + dir * (o.gap == null ? 3 : o.gap);
        const c = hiAll ? HOT : col(o.c, COL.slate);
        add('br', { type: 'poly', pts: [x0, y - dir * 1.1, x0, y, x1, y, x1, y - dir * 1.1], fill: 'none', stroke: c, sw: 0.2 });
        if (o.text != null) add('t', textP(o.text, (x0 + x1) / 2, y + dir * 2, { mono: 1, size: sz(o.size, 1.9), fill: c, halo: 1 }), { w: [0.4, 1] });
        A[id] = box(x0, y - 1.1, x1, y + 1.1, { kind: 'brace' });
        return;
      }
      if (!isNum(o.x)) { E('brace needs a numeric "x"'); return; }
      const y0 = Math.min(...ys) - 2, y1 = Math.max(...ys) + 2, x = o.x, right = (o.side || 'e') !== 'w';
      const hk = right ? -1.1 : 1.1;
      const c = hiAll ? HOT : col(o.c, COL.slate);
      add('br', { type: 'poly', pts: [x + hk, y0, x, y0, x, y1, x + hk, y1], fill: 'none', stroke: c, sw: 0.2 });
      if (o.text != null) add('t', textP(o.text, right ? x + 1.2 : x - 1.2, (y0 + y1) / 2, { mono: 1, size: sz(o.size, 1.9), fill: c, anchor: right ? 'start' : 'end', halo: 1 }), { w: [0.4, 1] });
      A[id] = box(Math.min(x, x + hk), y0, Math.max(x, x + hk), y1, { kind: 'brace' });
      return;
    }
    if (t === 'line') {
      const L = o.line;
      if (!Array.isArray(L) || L.length !== 4 || !L.every(isNum)) { E('line must be [x1, y1, x2, y2]'); return; }
      const c = hiAll ? HOT : col(o.c, COL.haze);
      add('line', { type: 'line', x1: L[0], y1: L[1], x2: L[2], y2: L[3], stroke: c, sw: isNum(o.sw) ? o.sw : 0.18, dash: o.dash ? '1.1 0.8' : null });
      if (o.text != null) { // label just beyond the first point, away from the second
        const dx = L[2] - L[0], dy = L[3] - L[1], len = Math.hypot(dx, dy) || 1, s2 = sz(o.size, 1.9);
        const vert = Math.abs(dy) > Math.abs(dx);
        const tx = vert ? L[0] : L[0] - dx / len * 1.2, ty = vert ? L[1] - dy / len * (1.0 + s2 * 0.45) : L[1];
        const anchor = vert ? 'middle' : (dx > 0 ? 'end' : 'start');
        add('t', textP(o.text, tx, ty, { mono: 1, size: s2, fill: c === COL.haze ? COL.slate : c, anchor, halo: 1 }), { w: [0.5, 1] });
      }
      A[id] = box(Math.min(L[0], L[2]), Math.min(L[1], L[3]), Math.max(L[0], L[2]), Math.max(L[1], L[3]), { kind: 'line' });
      return;
    }
    E('unknown type');
  }

  for (const id of objs.keys()) { cur = ''; need(id); }

  // focus: listed ids (globs allowed) stay bright, the rest dim
  const foc = st.focus ? [].concat(st.focus) : null;
  const prims = [];
  for (const id of objs.keys()) {
    const P = out[id]; if (!P) continue;
    const bright = !foc || foc.some(f => glob(f, id));
    for (const p of P) { if (!bright) p.o *= DIM; prims.push(p); }
  }
  return { prims, A, errs, warns };
}

/* header, caption, counter */
function headerPrims(st, total, deck) {
  const h = st.head, P = [];
  const add = (k, p) => { p.k = '_h/' + k; p.obj = '_h'; p.hdr = 1; if (p.o == null) p.o = 1; P.push(p); };
  if (h.kicker) add('kicker', textP(String(h.kicker).toUpperCase(), 6, 0, { base: 7.2, mono: 1, size: 2.05, fill: COL.slate, anchor: 'start', ls: 0.1 }));
  if (h.title) {
    const runs = splitRuns(h.title, COL.navy, COL.cobalt, 600) || [{ t: String(h.title), fill: COL.navy, weight: 600 }];
    add('title', textP(String(h.title), 6, 0, { base: 15.4, size: 6.0, fill: COL.navy, anchor: 'start', runs, weight: 600 }));
  }
  add('rule', { type: 'line', x1: 6.5, y1: 26.5, x2: 153.5, y2: 26.5, stroke: COL.rule, sw: 0.15 });
  const cap = (h.fig ? h.fig + '   ' : '') + (h.cap || '');
  if (cap) add('cap', textP(cap, 7.3, 0, { base: 86, mono: 1, size: 1.95, fill: COL.slate, anchor: 'start' }));
  if (!deck._noCounter) add('count', textP(`${st.n + 1} / ${total}`, 153.5, 0, { base: 86, mono: 1, size: 1.75, fill: COL.haze, anchor: 'end' }));
  return P;
}

function compile(deck) {
  const B = buildStates(deck);
  const slides = B.states.map(st => {
    // two passes: the second lets early objects also dodge objects that are laid out after them
    const L1 = layoutState(st, { ever: B.ever });
    const extra = [];
    for (const p of L1.prims) for (const sh of shapesOf(p)) { sh.obj = p.obj; extra.push(sh); }
    const L = layoutState(st, { ever: B.ever, extra });
    return { st, prims: headerPrims(st, B.states.length, deck).concat(L.prims), A: L.A, errs: L.errs, warns: L.warns };
  });
  const errs = B.errs.concat(...slides.map(s => s.errs));
  const warns = B.warns.concat(...slides.map(s => s.warns));
  return { deck, slides, errs, warns };
}

/* ---------------------------------------------------------------- motion */
const easeIO = q => q < 0.5 ? 4 * q * q * q : 1 - Math.pow(-2 * q + 2, 3) / 2;
const easeOut = q => 1 - Math.pow(1 - q, 3);
const mix = (a, b, p) => a + (b - a) * p;
function rgb(h) { const n = parseInt(h.slice(1), 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; }
function mixCol(a, b, p) {
  if (a === b) return b;
  if (!a || !b || a[0] !== '#' || b[0] !== '#') return p < 0.5 ? a : b;
  const x = rgb(a), y = rgb(b);
  return '#' + x.map((v, i) => Math.round(mix(v, y[i], p)).toString(16).padStart(2, '0')).join('');
}
const NUMK = ['x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'w', 'h', 'size', 'sw', 'o', 'fo', 'rx'];
const strOf = p => p.runs ? p.runs.map(r => r.t).join('') : p.str;
function lerpPrim(a, b, p) {
  const r = Object.assign({}, b);
  for (const k of NUMK) if (isNum(a[k]) && isNum(b[k])) r[k] = mix(a[k], b[k], p);
  for (const k of ['fill', 'stroke']) {
    if (a[k] === 'none' && b[k] && b[k] !== 'none') { r[k] = b[k]; if (b.type !== 'text') r.fo = (isNum(b.fo) ? b.fo : 1) * p; }
    else if (b[k] === 'none' && a[k] && a[k] !== 'none') { r[k] = a[k]; if (b.type !== 'text') r.fo = (isNum(a.fo) ? a.fo : 1) * (1 - p); }
    else r[k] = mixCol(a[k], b[k], p);
  }
  if (a.pts && b.pts && a.pts.length === b.pts.length) r.pts = a.pts.map((v, i) => mix(v, b.pts[i], p));
  if (a.runs && b.runs && a.runs.length === b.runs.length) r.runs = b.runs.map((rb, i) => Object.assign({}, rb, { fill: mixCol(a.runs[i].fill, rb.fill, p) }));
  return r;
}
function samePrim(a, b) {
  if (a.type !== b.type || strOf(a) !== strOf(b)) return false;
  for (const k of NUMK) if ((a[k] || 0) !== (b[k] || 0)) return false;
  if (a.fill !== b.fill || a.stroke !== b.stroke || a.dash !== b.dash) return false;
  if (a.pts && String(a.pts) !== String(b.pts)) return false;
  if (a.runs && JSON.stringify(a.runs) !== JSON.stringify(b.runs)) return false;
  return true;
}
function morph(a, b, e) {
  if (a.type !== b.type) return [Object.assign({}, a, { k: a.k + '~', o: a.o * (1 - e) }), Object.assign({}, b, { o: b.o * e })];
  const r = lerpPrim(a, b, e);
  if (r.type === 'text' && strOf(a) !== strOf(b)) {
    const na = parseFloat(a.str), nb = parseFloat(b.str);
    if (b.tween && /^-?[\d.]+$/.test(a.str) && /^-?[\d.]+$/.test(b.str)) {
      r.str = mix(na, nb, e).toFixed(Math.max(decOf(a.str), decOf(b.str)));
      return [r];
    }
    const old = Object.assign({}, r, { k: a.k + '~', str: a.str, runs: a.runs, o: a.o * (1 - e) });
    const nw = Object.assign({}, r, { o: b.o * e });
    if (b.roll) { old.y = r.y - r.size * 0.55 * e; nw.y = r.y + r.size * 0.55 * (1 - e); }
    else { old.o = a.o * clamp(1 - 2 * e); nw.o = b.o * clamp(2 * e - 1); } // out, then in: never two strings on top of each other
    return [old, nw];
  }
  return [r];
}
function enter(p, q) {
  if (q <= 0) return null;
  if (q >= 1) return p;
  const r = Object.assign({}, p);
  if (p.type === 'line') { r.x2 = mix(p.x1, p.x2, q); r.y2 = mix(p.y1, p.y2, q); r.o = p.o * Math.min(1, q * 4); }
  else if (p.type === 'circle') { r.r = p.r * q; }
  else if (p.type === 'rect') {
    if (p.ent === 'up') { r.y = p.y + p.h * (1 - q); r.h = p.h * q; }
    else { const s = 0.94 + 0.06 * q; r.x = p.x + p.w * (1 - s) / 2; r.y = p.y + p.h * (1 - s) / 2; r.w = p.w * s; r.h = p.h * s; r.o = p.o * q; }
  }
  else if (p.type === 'text') { r.o = p.o * q; r.y = p.y + 0.8 * (1 - q); }
  else r.o = p.o * q;
  return r;
}
// plan a transition from prims A to prims B; times in seconds
function plan(Ap, Bp, st, deck) {
  const amap = new Map(Ap.map(p => [p.k, p])), bmap = new Map(Bp.map(p => [p.k, p]));
  const aObj = new Set(Ap.map(p => p.obj));
  const items = [];
  let moved = false, exits = false;
  for (const p of Ap) if (!bmap.has(p.k)) exits = true;
  for (const p of Bp) { const a = amap.get(p.k); if (a && !samePrim(a, p)) moved = true; }
  const newObjs = [];
  for (const p of Bp) if (!aObj.has(p.obj) && !newObjs.includes(p.obj)) newObjs.push(p.obj);
  const D = st && st.dur != null ? st.dur : (deck && deck.dur != null ? deck.dur : 0.9);
  const N = newObjs.length, EN = Math.min(0.65, Math.max(0.45, D * 0.7));
  const spread = st && st.stagger != null ? st.stagger * Math.max(0, N - 1) : Math.min(0.2 * Math.max(0, N - 1), 0.55);
  const step = N > 1 ? spread / (N - 1) : 0;
  const s0 = (moved || exits) ? D * 0.4 : 0;
  const objStart = new Map(newObjs.map((id, i) => [id, s0 + i * step]));
  const from = new Map();
  if (st) for (const [id, o] of st.objs) if (o.from && o._from === st.n && objStart.has(id)) from.set(id, o.from);
  for (const p of Ap) if (!bmap.has(p.k)) items.push({ mode: 'exit', a: p, t0: 0, t1: D * 0.5 });
  for (const p of Bp) {
    const a = amap.get(p.k);
    if (a) { items.push(samePrim(a, p) ? { mode: 'same', b: p } : { mode: 'morph', a, b: p, t0: 0, t1: D }); continue; }
    const s = objStart.has(p.obj) ? objStart.get(p.obj) : (aObj.has(p.obj) ? D * 0.8 : s0);
    const src = from.get(p.obj);
    if (src) {
      const part = p.k.slice(p.obj.length + 1), sp = amap.get(src + '/' + part);
      if (sp) { items.push({ mode: 'morph', a: Object.assign({}, sp, { k: p.k }), b: p, t0: s, t1: s + D }); continue; }
    }
    const w = Array.isArray(p.w) ? p.w : [0, 1];
    items.push({ mode: 'enter', b: p, t0: s + w[0] * EN, t1: s + w[1] * EN });
  }
  let T = 0;
  for (const it of items) if (it.t1 > T) T = it.t1;
  return { items, T: Math.max(T, 0.001) };
}
function frameAt(pl, t) {
  const out = [];
  for (const it of pl.items) {
    if (it.mode === 'same') { out.push(it.b); continue; }
    const q = clamp((t - it.t0) / (it.t1 - it.t0));
    if (it.mode === 'exit') { if (q < 1) out.push(Object.assign({}, it.a, { k: it.a.k + '^', o: it.a.o * (1 - easeIO(q)) })); continue; }
    if (it.mode === 'enter') { const p = enter(it.b, easeOut(q)); if (p) out.push(p); continue; }
    for (const p of morph(it.a, it.b, easeIO(q))) out.push(p);
  }
  return out;
}

/* ---------------------------------------------------------------- DOM */
const NS = 'http://www.w3.org/2000/svg';
const r3 = v => Math.round(v * 1000) / 1000;
function Renderer(svg) { this.svg = svg; this.els = new Map(); this.sig = ''; }
Renderer.prototype.draw = function (prims) {
  const seen = new Set(), keys = [];
  for (const p of prims) {
    if (!p || seen.has(p.k)) continue;
    let el = this.els.get(p.k);
    const tag = p.type === 'poly' ? (p.closed ? 'polygon' : 'polyline') : p.type;
    if (!el || el.localName !== tag) { if (el) el.remove(); el = document.createElementNS(NS, tag); this.els.set(p.k, el); this.sig = ''; }
    apply(el, p);
    seen.add(p.k); keys.push(p.k);
  }
  for (const [k, el] of this.els) if (!seen.has(k)) { el.remove(); this.els.delete(k); this.sig = ''; }
  const sig = keys.join('|');
  if (sig !== this.sig) { for (const k of keys) this.svg.appendChild(this.els.get(k)); this.sig = sig; }
};
function set(el, k, v) { const s = v == null ? null : (typeof v === 'number' ? String(r3(v)) : v); if (el['_' + k] === s) return; el['_' + k] = s; if (s == null) el.removeAttribute(k); else el.setAttribute(k, s); }
function apply(el, p) {
  set(el, 'opacity', clamp(p.o));
  if (p.type === 'line') {
    set(el, 'x1', p.x1); set(el, 'y1', p.y1); set(el, 'x2', p.x2); set(el, 'y2', p.y2);
    set(el, 'stroke', p.stroke); set(el, 'stroke-width', p.sw); set(el, 'stroke-dasharray', p.dash || null); set(el, 'stroke-linecap', p.dash ? 'butt' : 'round');
  } else if (p.type === 'circle') {
    set(el, 'cx', p.cx); set(el, 'cy', p.cy); set(el, 'r', Math.max(0, p.r)); set(el, 'fill', p.fill); set(el, 'stroke', p.stroke || 'none'); set(el, 'stroke-width', p.sw || 0);
  } else if (p.type === 'rect') {
    set(el, 'x', p.x); set(el, 'y', p.y); set(el, 'width', Math.max(0, p.w)); set(el, 'height', Math.max(0, p.h)); set(el, 'rx', p.rx || 0);
    set(el, 'fill', p.fill); set(el, 'fill-opacity', p.fill === 'none' ? null : (isNum(p.fo) ? clamp(p.fo) : 1));
    set(el, 'stroke', p.stroke || 'none'); set(el, 'stroke-width', p.sw || 0); set(el, 'stroke-dasharray', p.dash || null);
  } else if (p.type === 'poly') {
    set(el, 'points', p.pts.map(r3).join(' ')); set(el, 'fill', p.fill || 'none'); set(el, 'stroke', p.stroke || 'none'); set(el, 'stroke-width', p.sw || 0);
    set(el, 'stroke-linejoin', 'round'); set(el, 'stroke-linecap', 'round');
  } else if (p.type === 'text') {
    set(el, 'x', p.x); set(el, 'y', p.y); set(el, 'font-size', p.size); set(el, 'fill', p.fill);
    set(el, 'text-anchor', p.anchor);
    set(el, 'font-style', p.italic ? 'italic' : null); set(el, 'font-weight', p.weight || null);
    set(el, 'letter-spacing', p.ls ? r3(p.ls * p.size) : null);
    set(el, 'class', (p.mono ? 'm' : 's') + (p.halo ? ' h' : ''));
    const key = p.runs ? JSON.stringify(p.runs) : p.str;
    if (el._txt !== key) {
      el._txt = key;
      el.textContent = '';
      if (p.runs) for (const r of p.runs) {
        const ts = document.createElementNS(NS, 'tspan');
        ts.textContent = r.t;
        if (r.fill) ts.setAttribute('fill', r.fill);
        if (r.italic) ts.setAttribute('font-style', 'italic');
        if (r.weight) ts.setAttribute('font-weight', r.weight);
        el.appendChild(ts);
      } else el.textContent = p.str;
    }
  }
}

/* ---------------------------------------------------------------- player */
function Player(svg, deck, opts) {
  this.svg = svg; this.R = new Renderer(svg); this.opts = opts || {};
  this.load(deck);
}
Player.prototype.load = function (deck, keepIndex) {
  this.C = compile(deck);
  this.deck = deck;
  this.n = this.C.slides.length;
  if (!keepIndex) this.i = -1;
  this.i = Math.min(this.i, this.n - 1);
  this.anim = null;
  if (this.opts.onErrors) this.opts.onErrors(this.C.errs, this.C.warns);
};
Player.prototype.primsOf = function (i) { return i < 0 ? [] : this.C.slides[i].prims; };
Player.prototype.show = function (i) { this.stop(); this.i = i; this.R.draw(this.primsOf(i)); this.notify(); };
Player.prototype.freeze = function (i, frac) {
  const pl = plan(this.primsOf(i - 1), this.primsOf(i), this.C.slides[i].st, this.deck);
  this.i = i; this.R.draw(frameAt(pl, frac * pl.T)); this.notify();
  return pl.T;
};
Player.prototype.stop = function () { if (this.anim) { cancelAnimationFrame(this.anim.raf); this.anim = null; } };
Player.prototype.go = function (j) {
  if (j < 0 || j >= this.n || j === this.i) return false;
  const fromPr = this.anim ? this.primsOf(this.i) : this.primsOf(this.i);
  this.stop();
  const pl = plan(fromPr, this.primsOf(j), this.C.slides[j].st, this.deck);
  this.i = j; this.notify();
  const t0 = performance.now(), self = this;
  const tick = now => {
    const t = (now - t0) / 1000;
    self.R.draw(frameAt(pl, Math.min(t, pl.T)));
    if (t < pl.T) self.anim.raf = requestAnimationFrame(tick);
    else { self.anim = null; if (self.opts.onDone) self.opts.onDone(j); }
  };
  this.anim = { raf: requestAnimationFrame(tick) };
  return true;
};
Player.prototype.next = function () { return this.go(this.i + 1); };
Player.prototype.prev = function () { return this.go(this.i - 1); };
Player.prototype.notify = function () { if (this.opts.onStep) this.opts.onStep(this.i, this.n); };

const api = { W, H, COL, SZ, TYPES, TOP, BOTTOM, buildStates, layoutState, compile, plan, frameAt, textBox, shapesOf, boxOverlap, segHitsBox, Renderer, Player };
return api;
})();
export default DeckEngine;
